// eslint-disable-next-line no-restricted-imports
import { toast } from "react-toastify";

import { serverRefreshGitbook } from "api/api-server-ai";
import { AIModelType } from "components/server-types";
import { t } from "i18n/i18n";
import { IconText } from "mds/components/IconText";
import { Menu, MenuHeader, MenuItem, MenuRadioGroup, SubMenu } from "mds/components/Menu";
import { SquareButton } from "mds/components/buttons/SquareButton";
import { GearSettingsIcon, RefreshIcon } from "mds/icons";
import { useAppDispatch, useAppSelector } from "store";
import { useCapabilities } from "store/capability-hooks";
import { selectUserSelectedAIModel } from "store/selectors";
import { aiStateActions } from "store/slices/ai";
import { toastErrorMessage, toastSuccessMessage } from "utils/alerts";

// Default model for the chat panel, should be fast and mainly rely on good context to give good responses.

export const DEFAULT_MODEL_FOR_CHAT: AIModelType = AIModelType.GPT_5_NANO;

// For assessment, ensure we use a multimodal model that can read files (images/PDFs,etc.)

export const DEFAULT_MODEL_FOR_ASSESSMENT_QUERY: AIModelType = AIModelType.GPT_5_MINI;

const handleRefreshGitbook = async () => {
  const toastId = toast.loading("Refreshing Gitbook pages...");

  try {
    await serverRefreshGitbook();
    toast.dismiss(toastId);
    toastSuccessMessage("Gitbook pages refreshed successfully");
  } catch (error) {
    console.error(error);
    toast.dismiss(toastId);
    toastErrorMessage("Error refreshing Gitbook pages");
  }
};

export const AISettings = () => {
  const dispatch = useAppDispatch();
  const { canUseBetaFeatures } = useCapabilities();
  const userSelectedAIModel = useAppSelector(selectUserSelectedAIModel);

  return (
    <Menu
      direction="bottom"
      menuButton={
        <SquareButton aria-label={t("ai.settings.title")} kind="tertiary" size="s">
          <GearSettingsIcon />
        </SquareButton>
      }
    >
      <MenuHeader>{t("ai.settings.title")}</MenuHeader>
      {canUseBetaFeatures && (
        <MenuItem disableWhilePending onClick={handleRefreshGitbook}>
          <IconText iconStart={<RefreshIcon />} text={t("ai.settings.refresh_gitbook")} />
        </MenuItem>
      )}
      <SubMenu label={t("ai.settings.model")}>
        <MenuRadioGroup
          value={userSelectedAIModel}
          onRadioChange={(e) =>
            dispatch(aiStateActions.setUserSelectedAIModel(e.value as AIModelType | null))
          }
        >
          {[null, ...Object.values(AIModelType)].map((modelOption) => (
            <MenuItem key={modelOption ?? "default"} type="radio" value={modelOption}>
              {/* Users can select "Default" to clear their local storage and revert to using the default */}
              {/* model (DEFAULT_MODEL_FOR_CHAT or DEFAULT_MODEL_FOR_ASSESSMENT_QUERY) again. */}
              {modelOption ? modelOption.toUpperCase() : t("ai.settings.default_model")}
            </MenuItem>
          ))}
        </MenuRadioGroup>
      </SubMenu>
    </Menu>
  );
};
